import xappmediaProducts from "generated/products-xappmedia";
import xappaiProducts from "generated/products-xapp-ai";

export interface Product {
    id: string;
    name: string;
    active: boolean;
    description?: string;
    caption?: string;
    images?: string[];
    priceInCents?: number;
    price?: string;
    metadata?: {
        legacy?: string;
        chat?: string;
        gbm?: string;
        search?: string;
        form?: string;
        partner?: "lacrm" | "jobber" | string;
        ["live-chat"]?: string;
        integrations?: string;
        sku?: string;
    };
}

const DEV_PRODUCT: Product = {
    id: "prod_QgzDK1DTytsTCl",
    name: "XAPP AI Starter DEV",
    active: true,
    description: "XAPP AI Starter DEV",
    metadata: {},
    priceInCents: 1995,
    price: "$19.95 / month",
};

export function getProduct(productId: string) {
    const products: Product[] = [];

    // push both sets of products
    products.push(...xappmediaProducts);
    products.push(...xappaiProducts);

    const foundProduct = products.find((product) => {
        return product.id === productId;
    });

    // dev product is defined here because we override it with the other lists
    if (productId === "prod_QgzDK1DTytsTCl") {
        return DEV_PRODUCT;
    }

    if (!foundProduct) {
        return {
            id: productId,
            name: "Unknown Product",
            active: false,
            description: "This product is not available.",
            metadata: {
                legacy: "false",
                chat: "false",
                gbm: "false",
                search: "false",
                form: "false",
                partner: "",
                ["live-chat"]: "false",
                integrations: "false",
            },
        };
    } else {
        return foundProduct;
    }
}

export function getProducts(filter?: { partner?: string; legacy?: boolean; id?: string }): Product[] {
    // Pull in the correct set of products depending on environment

    let products: Product[] = [];

    if (__ENV__ === "development") {
        // push both sets of products
        products.push(...xappmediaProducts);
        products.push(...xappaiProducts);

        // push our dev product
        products.push(DEV_PRODUCT);
    } else {
        if (__ACCOUNT__ === "stentor") {
            // just for testing products.push(...xappaiProducts);
            products.push(...xappmediaProducts);
        } else if (__ACCOUNT__ === "studio") {
            products.push(...xappaiProducts);
        }
    }

    if (filter) {
        const { partner, legacy } = filter;

        if (partner) {
            const filtered = products.filter((product) => {
                return product?.metadata?.partner === partner;
            });

            products = filtered;
        }

        // slightly different, if partner is undefined we remove those with defined partners
        if (typeof partner === "undefined") {
            const filtered = products.filter((product) => {
                return !product?.metadata?.partner;
            });

            products = filtered;
        }

        if (typeof legacy === "boolean") {
            const lookingForLegacy = legacy ? "true" : "false";

            const filtered = products.filter((product) => {
                const legacy = product.metadata?.legacy;

                // if there isn't a value and lookingForLegacy is "false", we filter it out
                if (!legacy && lookingForLegacy === "false") {
                    return true;
                }

                return product.metadata?.legacy === lookingForLegacy;
            });

            products = filtered;
        }

        if (typeof filter.id === "string") {
            const filtered = products.filter((product) => {
                return product.id === filter.id;
            });

            products = filtered;
        }
    }

    return products;
}

export function supportsIntegrations(productId: string): boolean {
    const product = getProducts().find((product) => product.id === productId);

    if (!product) {
        return false;
    }

    // one try, these IDs include integrations
    const INTEGRATED = ["prod_QWS83N4REsR3LV", "prod_QhLSfUmtdtuBIT", "prod_QjZlYgutfcGFGN"];
    if (INTEGRATED.includes(productId)) {
        return true;
    }
    // otherwise check the metadata
    return product.metadata?.integrations?.toLowerCase() === "true";
}

export function supportsSearch(productId: string): boolean {
    const product = getProducts().find((product) => product.id === productId);

    if (!product) {
        return false;
    }

    return product.metadata?.search?.toLowerCase() === "true";
}

export function getDefaultProduct(partner?: string): string {
    if (partner === "angi") {
        return "prod_S5qiybsJZ2Mt6D";
    }

    // OUR DEV STARTER or our XAPP AI Leads 2024 Product
    return __ENV__ === "development" ? "prod_QgzDK1DTytsTCl" : "prod_S5qiybsJZ2Mt6D";
}

/**
 * @deprecated - We don't have GBM channel anymore
 */
export function supportsGBM(productId: string): boolean {
    const product = getProducts().find((product) => product.id === productId);

    if (!product) {
        return false;
    }

    return product.metadata?.gbm?.toLowerCase() === "true";
}

export function supportsLiveChat(productId: string): boolean {
    const product = getProducts().find((product) => product.id === productId);

    if (!product) {
        return false;
    }

    return product.metadata?.["live-chat"]?.toLowerCase() === "true";
}
